import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import CustomTooltip from "./custom-tooltip";
import NoSqlCollectionDialog from "./nosql-collection-dialog";
import NoSqlFieldDialog from "./nosql-field-dialog";
import NoSqlIndexDialog from "./nosql-index-dialog";
import {
  NoSqlOntologyCollection,
  NoSqlOntologyField,
  NoSqlOntologyIndex,
  useDeleteNoSqlCollection,
  useDeleteNoSqlField,
  useDeleteNoSqlIndex,
  useGetNoSqlCollections,
  useGetNoSqlFields,
  useGetNoSqlIndexes,
} from "@/service-hooks/use-get-nosql-ontology-service";

interface NoSqlOntologySectionProps {
  projectUuid: string;
  dataLakeId: string;
  dataLakeLabel?: string;
  isViewer?: boolean;
}

const PAGE_SIZE = 5;

/**
 * Collections panel for a Non-Relational (NoSQL) DataLake. Mirrors the DDL
 * Tables → Columns and ES Indexes → Fields pattern: the Collections card lists
 * selectable rows, and selecting one reveals sibling Fields and Indexes cards
 * below. Engine-agnostic — a "collection" is a Mongo collection, a Dynamo
 * table, a FHIR resource type, etc.
 */
export default function NoSqlOntologySection({
  projectUuid,
  dataLakeId,
  dataLakeLabel,
  isViewer,
}: NoSqlOntologySectionProps) {
  const collectionsQuery = useGetNoSqlCollections(dataLakeId, true, { projectUuid });
  const deleteCollection = useDeleteNoSqlCollection();

  const [selectedCollectionId, setSelectedCollectionId] = useState<string | null>(null);
  const [collectionPage, setCollectionPage] = useState(1);
  const [collectionDialogOpen, setCollectionDialogOpen] = useState(false);
  const [editingCollection, setEditingCollection] = useState<NoSqlOntologyCollection | undefined>();

  const collections = collectionsQuery.data ?? [];
  const selectedCollection = selectedCollectionId
    ? collections.find((c) => c.id === selectedCollectionId) ?? null
    : null;

  useEffect(() => {
    setSelectedCollectionId(null);
    setCollectionPage(1);
  }, [dataLakeId]);

  const total = collections.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const start = (collectionPage - 1) * PAGE_SIZE;
  const pageItems = collections.slice(start, start + PAGE_SIZE);

  const toggleSelect = (id?: string) => {
    if (!id) return;
    setSelectedCollectionId((prev) => (prev === id ? null : id));
  };

  const onClickAdd = () => {
    if (isViewer) {
      toast.error("You have view-only access to this project");
      return;
    }
    setEditingCollection(undefined);
    setCollectionDialogOpen(true);
  };

  const onClickEdit = (c: NoSqlOntologyCollection) => {
    setEditingCollection(c);
    setCollectionDialogOpen(true);
  };

  const onClickDelete = async (c: NoSqlOntologyCollection) => {
    if (!c.id) return;
    try {
      await deleteCollection.mutateAsync({ id: c.id, projectUuid, dataLakeId });
      setSelectedCollectionId((prev) => (prev === c.id ? null : prev));
    } catch {
      // toast handled in mutation
    }
  };

  return (
    <>
      {/* Collections card — selectable rows */}
      <div className="bg-surface-card rounded-lg p-4 border border-surface-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <h4 className="text-lg font-semibold text-white">Collections</h4>
            <span className="text-sm text-muted bg-surface-dark px-2 py-1 rounded">
              {`${pageItems.length} of ${total}`}
            </span>
            {dataLakeLabel && (
              <span className="text-sm text-white truncate">— {dataLakeLabel}</span>
            )}
            {collectionsQuery.isFetching && (
              <Loader2 className="w-4 h-4 animate-spin text-muted shrink-0" />
            )}
          </div>
          {!isViewer && (
            <CustomTooltip text="Add Collection">
              <Button
                variant="outline"
                size="icon"
                className="bg-primary hover:bg-primary-dark"
                onClick={(e) => {
                  e.stopPropagation();
                  onClickAdd();
                }}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </CustomTooltip>
          )}
        </div>

        {collections.length === 0 ? (
          collectionsQuery.isFetching ? (
            <div className="text-xs text-muted text-center py-3 flex items-center justify-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Loading collections...
            </div>
          ) : (
            <div className="text-xs text-muted text-center py-3">
              No collections yet. Click + to add one.
            </div>
          )
        ) : (
          <div className="grid grid-cols-1 gap-2">
            {pageItems.map((c) => {
              const isSelected = !!c.id && selectedCollectionId === c.id;
              return (
                <div
                  key={c.id ?? c.name}
                  className={`relative group flex items-center justify-between px-3 py-2.5 rounded border transition-colors ${
                    isSelected
                      ? "border-primary bg-primary/10"
                      : "border-surface-border bg-surface-active hover:bg-surface-hover"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => toggleSelect(c.id)}
                    className="flex-1 min-w-0 text-left text-white"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-base font-medium truncate min-w-0" title={c.name}>
                        {c.name}
                      </span>
                      <span className="text-xs text-muted whitespace-nowrap shrink-0">
                        {c.fieldCount ?? 0} field{(c.fieldCount ?? 0) === 1 ? "" : "s"}
                      </span>
                      {c.source && (
                        <span className="text-xs text-muted whitespace-nowrap shrink-0">
                          {c.source}
                        </span>
                      )}
                    </div>
                    {c.description && (
                      <div className="text-xs text-muted truncate mt-0.5" title={c.description}>
                        {c.description}
                      </div>
                    )}
                  </button>
                  {!isViewer && (
                    <div className="flex gap-2 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      <CustomTooltip text="Edit Collection">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            onClickEdit(c);
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </CustomTooltip>
                      <CustomTooltip text="Delete Collection">
                        <Button
                          variant="destructive"
                          size="icon"
                          disabled={!c.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            onClickDelete(c);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </CustomTooltip>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between px-2 py-2 mt-3 bg-surface-active rounded border border-surface-border">
            <span className="text-sm text-blue-400">
              Page {collectionPage} of {totalPages} ({pageItems.length} collections this page) • Total: {total} collections
            </span>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setCollectionPage((p) => Math.max(1, p - 1))}
                disabled={collectionPage === 1}
                className="h-8 w-8 p-0 bg-surface-active border-surface-border text-white hover:bg-surface-hover disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setCollectionPage((p) => Math.min(totalPages, p + 1))}
                disabled={collectionPage === totalPages}
                className="h-8 w-8 p-0 bg-surface-active border-surface-border text-white hover:bg-surface-hover disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Fields & Indexes cards — only when a collection is selected */}
      {selectedCollection && selectedCollection.id && (
        <CollectionChildren
          projectUuid={projectUuid}
          dataLakeId={dataLakeId}
          collectionId={selectedCollection.id}
          collectionName={selectedCollection.name}
          isViewer={isViewer}
        />
      )}

      <NoSqlCollectionDialog
        isOpen={collectionDialogOpen}
        onClose={() => setCollectionDialogOpen(false)}
        projectUuid={projectUuid}
        dataLakeId={dataLakeId}
        existing={editingCollection}
      />
    </>
  );
}

interface CollectionChildrenProps {
  projectUuid: string;
  dataLakeId: string;
  collectionId: string;
  collectionName?: string;
  isViewer?: boolean;
}

/**
 * Fields and Indexes panels for the selected NoSQLCollection, rendered as two
 * sibling cards below the Collections list. Both lists paginate client-side.
 */
function CollectionChildren({
  projectUuid,
  dataLakeId,
  collectionId,
  collectionName,
  isViewer,
}: CollectionChildrenProps) {
  const fieldsQuery = useGetNoSqlFields(dataLakeId, true, { projectUuid, collectionId, limit: 1000 });
  const indexesQuery = useGetNoSqlIndexes(dataLakeId, true, { projectUuid, collectionId, limit: 1000 });
  const deleteField = useDeleteNoSqlField();
  const deleteIndex = useDeleteNoSqlIndex();

  const [fieldDialogOpen, setFieldDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState<NoSqlOntologyField | undefined>();
  const [indexDialogOpen, setIndexDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<NoSqlOntologyIndex | undefined>();

  const [fieldPage, setFieldPage] = useState(1);
  const [indexPage, setIndexPage] = useState(1);

  useEffect(() => {
    setFieldPage(1);
    setIndexPage(1);
  }, [collectionId]);

  const fields = fieldsQuery.data ?? [];
  const indexes = indexesQuery.data ?? [];

  const fieldTotal = fields.length;
  const fieldTotalPages = Math.max(1, Math.ceil(fieldTotal / PAGE_SIZE));
  const fieldStart = (fieldPage - 1) * PAGE_SIZE;
  const fieldPageItems = fields.slice(fieldStart, fieldStart + PAGE_SIZE);

  const indexTotal = indexes.length;
  const indexTotalPages = Math.max(1, Math.ceil(indexTotal / PAGE_SIZE));
  const indexStart = (indexPage - 1) * PAGE_SIZE;
  const indexPageItems = indexes.slice(indexStart, indexStart + PAGE_SIZE);

  const openAddField = () => {
    if (isViewer) return;
    setEditingField(undefined);
    setFieldDialogOpen(true);
  };
  const openEditField = (f: NoSqlOntologyField) => {
    setEditingField(f);
    setFieldDialogOpen(true);
  };
  const onDeleteField = async (f: NoSqlOntologyField) => {
    if (!f.id) return;
    try {
      await deleteField.mutateAsync({ id: f.id, projectUuid, dataLakeId });
    } catch {
      /* toast in mutation */
    }
  };

  const openAddIndex = () => {
    if (isViewer) return;
    setEditingIndex(undefined);
    setIndexDialogOpen(true);
  };
  const openEditIndex = (i: NoSqlOntologyIndex) => {
    setEditingIndex(i);
    setIndexDialogOpen(true);
  };
  const onDeleteIndex = async (i: NoSqlOntologyIndex) => {
    if (!i.id) return;
    try {
      await deleteIndex.mutateAsync({ id: i.id, projectUuid, dataLakeId });
    } catch {
      /* toast in mutation */
    }
  };

  return (
    <>
      {/* Fields card */}
      <div className="bg-surface-card rounded-lg p-4 border border-surface-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <h4 className="text-lg font-semibold text-white">Fields</h4>
            <span className="text-sm text-muted bg-surface-dark px-2 py-1 rounded">
              {`${fieldPageItems.length} of ${fieldTotal}`}
            </span>
            {collectionName && (
              <span className="text-sm text-white truncate" title={collectionName}>
                — {collectionName}
              </span>
            )}
            {fieldsQuery.isFetching && (
              <Loader2 className="w-4 h-4 animate-spin text-muted shrink-0" />
            )}
          </div>
          {!isViewer && (
            <CustomTooltip text="Add Field">
              <Button
                variant="outline"
                size="icon"
                className="bg-primary hover:bg-primary-dark"
                onClick={(e) => {
                  e.stopPropagation();
                  openAddField();
                }}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </CustomTooltip>
          )}
        </div>

        {fieldPageItems.length > 0 ? (
          <div className="space-y-1.5">
            {fieldPageItems.map((f) => (
              <div
                key={f.id ?? f.fullPath}
                className="relative group/field flex items-start justify-between gap-2 border border-surface-border rounded bg-surface p-2.5 text-white"
              >
                <div className="min-w-0">
                  <div className="text-sm font-mono truncate" title={f.fullPath}>
                    {f.fullPath}
                  </div>
                  <div className="text-xs text-muted">
                    {f.dataType}
                    {f.nullable === false ? " · required" : ""}
                    {f.parentPath ? ` · nested under ${f.parentPath}` : ""}
                  </div>
                </div>
                {!isViewer && (
                  <div className="flex gap-1.5 shrink-0 items-center opacity-0 group-hover/field:opacity-100 transition-opacity">
                    <CustomTooltip text="Edit Field">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-muted hover:text-white hover:bg-surface-hover"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditField(f);
                        }}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    </CustomTooltip>
                    <CustomTooltip text="Delete Field">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={!f.id}
                        className="h-7 w-7 p-0 text-muted hover:text-error hover:bg-surface-hover disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteField(f);
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </CustomTooltip>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : fieldsQuery.isFetching ? (
          <div className="text-xs text-muted text-center py-2 flex items-center justify-center gap-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Loading fields...
          </div>
        ) : (
          <div className="text-xs text-muted text-center py-2">
            No fields yet. Click + to add one.
          </div>
        )}

        {fieldTotal > PAGE_SIZE && (
          <div className="flex items-center justify-between px-2 py-2 mt-3 bg-surface-active rounded border border-surface-border">
            <span className="text-sm text-blue-400">
              Page {fieldPage} of {fieldTotalPages} ({fieldPageItems.length} fields this page) • Total: {fieldTotal} fields
            </span>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setFieldPage((p) => Math.max(1, p - 1))}
                disabled={fieldPage === 1}
                className="h-8 w-8 p-0 bg-surface-active border-surface-border text-white hover:bg-surface-hover disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setFieldPage((p) => Math.min(fieldTotalPages, p + 1))}
                disabled={fieldPage === fieldTotalPages}
                className="h-8 w-8 p-0 bg-surface-active border-surface-border text-white hover:bg-surface-hover disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Indexes card */}
      <div className="bg-surface-card rounded-lg p-4 border border-surface-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <h4 className="text-lg font-semibold text-white">Indexes</h4>
            <span className="text-sm text-muted bg-surface-dark px-2 py-1 rounded">
              {`${indexPageItems.length} of ${indexTotal}`}
            </span>
            {collectionName && (
              <span className="text-sm text-white truncate" title={collectionName}>
                — {collectionName}
              </span>
            )}
            {indexesQuery.isFetching && (
              <Loader2 className="w-4 h-4 animate-spin text-muted shrink-0" />
            )}
          </div>
          {!isViewer && (
            <CustomTooltip text="Add Index">
              <Button
                variant="outline"
                size="icon"
                className="bg-primary hover:bg-primary-dark"
                onClick={(e) => {
                  e.stopPropagation();
                  openAddIndex();
                }}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </CustomTooltip>
          )}
        </div>

        {indexPageItems.length > 0 ? (
          <div className="space-y-1.5">
            {indexPageItems.map((i) => (
              <div
                key={i.id ?? i.name}
                className="relative group/index flex items-start justify-between gap-2 border border-surface-border rounded bg-surface p-2.5 text-white"
              >
                <div className="min-w-0">
                  <div className="text-sm font-mono truncate" title={i.name}>
                    {i.name}
                  </div>
                  <div className="text-xs text-muted truncate">
                    {(i.fields ?? []).join(", ") || "—"}
                    {i.unique ? " · unique" : ""}
                  </div>
                </div>
                {!isViewer && (
                  <div className="flex gap-1.5 shrink-0 items-center opacity-0 group-hover/index:opacity-100 transition-opacity">
                    <CustomTooltip text="Edit Index">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-muted hover:text-white hover:bg-surface-hover"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditIndex(i);
                        }}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    </CustomTooltip>
                    <CustomTooltip text="Delete Index">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={!i.id}
                        className="h-7 w-7 p-0 text-muted hover:text-error hover:bg-surface-hover disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteIndex(i);
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </CustomTooltip>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : indexesQuery.isFetching ? (
          <div className="text-xs text-muted text-center py-2 flex items-center justify-center gap-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Loading indexes...
          </div>
        ) : (
          <div className="text-xs text-muted text-center py-2">
            No indexes yet. Click + to add one.
          </div>
        )}

        {indexTotal > PAGE_SIZE && (
          <div className="flex items-center justify-between px-2 py-2 mt-3 bg-surface-active rounded border border-surface-border">
            <span className="text-sm text-blue-400">
              Page {indexPage} of {indexTotalPages} ({indexPageItems.length} indexes this page) • Total: {indexTotal} indexes
            </span>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIndexPage((p) => Math.max(1, p - 1))}
                disabled={indexPage === 1}
                className="h-8 w-8 p-0 bg-surface-active border-surface-border text-white hover:bg-surface-hover disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIndexPage((p) => Math.min(indexTotalPages, p + 1))}
                disabled={indexPage === indexTotalPages}
                className="h-8 w-8 p-0 bg-surface-active border-surface-border text-white hover:bg-surface-hover disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <NoSqlFieldDialog
        isOpen={fieldDialogOpen}
        onClose={() => setFieldDialogOpen(false)}
        projectUuid={projectUuid}
        dataLakeId={dataLakeId}
        collectionId={collectionId}
        collectionName={collectionName}
        existing={editingField}
      />
      <NoSqlIndexDialog
        isOpen={indexDialogOpen}
        onClose={() => setIndexDialogOpen(false)}
        projectUuid={projectUuid}
        dataLakeId={dataLakeId}
        collectionId={collectionId}
        collectionName={collectionName}
        existing={editingIndex}
      />
    </>
  );
}
